// Conectar com IA: o consentimento do OAuth, as conexões da loja e o catálogo
// de ferramentas (o mesmo do servidor MCP) que o WebMCP registra no navegador.
// Chaves de consulta: ['conectar-ia', loja, ...].
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api/api';
import { useLoja } from '../../shared/estado/loja';
import type { ConexoesDoMcp, FerramentasDoPainel, ResultadoDaFerramenta, RetornoDoConsentimento, TelaDeConsentimento } from '../../shared/api/tipos-gerados';

export type Conexao = ConexoesDoMcp['conexoes'][number];
export type FerramentaDoPainel = FerramentasDoPainel['ferramentas'][number];
export type Escopo = TelaDeConsentimento['escopos'][number]['id'];

/** Os parâmetros do OAuth que o servidor mandou na URL do consentimento, tal como vieram. */
export type ParametrosDaAutorizacao = Record<string, string>;
const NOMES = ['response_type', 'client_id', 'redirect_uri', 'state', 'scope', 'code_challenge', 'code_challenge_method', 'resource'] as const;
export function parametrosDaUrl(busca: string): ParametrosDaAutorizacao {
  const q = new URLSearchParams(busca);
  return Object.fromEntries(NOMES.flatMap((n) => (q.has(n) ? [[n, q.get(n) ?? '']] : [])));
}

export function useTelaDeConsentimento(p: ParametrosDaAutorizacao) {
  return useQuery({
    queryKey: ['conectar-ia', 'consentimento', p],
    queryFn: () => api.get<TelaDeConsentimento>('/api/mcp/autorizacao?' + new URLSearchParams(p).toString()),
    retry: false,
    staleTime: Infinity,
  });
}
export const aprovar = (parametros: ParametrosDaAutorizacao, tenantId: string, escopos: Escopo[]) =>
  api.post<RetornoDoConsentimento>('/api/mcp/autorizacao', { parametros, tenantId, escopos }).then((r) => r.redirect);
export const recusar = (parametros: ParametrosDaAutorizacao) =>
  api.post<RetornoDoConsentimento>('/api/mcp/autorizacao/recusar', { parametros }).then((r) => r.redirect);

const base = (loja?: string | null) => `/api/tenant/${encodeURIComponent(loja ?? useLoja.getState().tenantId ?? '')}/mcp`;

export function useConexoes() {
  const loja = useLoja((s) => s.tenantId) ?? '';
  return useQuery({ queryKey: ['conectar-ia', loja, 'conexoes'], queryFn: () => api.get<ConexoesDoMcp>(`${base(loja)}/conexoes`), enabled: loja !== '' });
}
export function useRecarregarConexoes() {
  const consultas = useQueryClient();
  const loja = useLoja((s) => s.tenantId) ?? '';
  return () => consultas.invalidateQueries({ queryKey: ['conectar-ia', loja, 'conexoes'] });
}
export const desconectar = (id: string) => api.delete<{ ok: true }>(`${base()}/conexoes/${encodeURIComponent(id)}`);

export const listarFerramentas = (loja: string) => api.get<FerramentasDoPainel>(`${base(loja)}/ferramentas`).then((r) => r.ferramentas);
export const executarFerramenta = (loja: string, nome: string, argumentos: Record<string, unknown>) =>
  api.post<ResultadoDaFerramenta>(`${base(loja)}/ferramentas/${encodeURIComponent(nome)}`, { argumentos });

/** Os rótulos das permissões (o servidor manda na tela de consentimento; aqui, para a lista de conexões). */
export const ROTULO_DO_ESCOPO: Record<Escopo, string> = {
  estudio: 'Estúdio',
  design: 'Design',
  instagram: 'Instagram',
  automacoes: 'Automações',
  transmissao: 'Transmissão',
  agentes: 'Agentes de atendimento',
  'anuncios:ler': 'Ver anúncios',
  'anuncios:gerir': 'Gerenciar anúncios',
};
